import { useEffect, useRef, useState } from 'react';
import { useBarcodePermission } from './useBarcodePermission';
import { WbButton, WbFlag, WbInput, WbModal } from '../wb';

interface BarcodeScannerProps {
  onDetected: (code: string) => void;
  onClose: () => void;
  onManualEntry?: (code: string) => void;
}

type ScannerControls = { stop: () => void };

/**
 * Barcode-scanner in een WbModal 'volledig'.
 *
 * Dynamically imports @zxing/browser so the decoder payload
 * only loads when the scanner actually opens.
 *
 * Supports alle courante label-formaten (Code128, Code39, DataMatrix,
 * QR, PDF417, ITF, EAN, Codabar). Forceert de achter-camera op mobile.
 * Zoeker-kader 2 px, gelezen code mono, Handmatig/Annuleren op 64 px onderin.
 */
export function BarcodeScanner({ onDetected, onClose, onManualEntry }: BarcodeScannerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const controlsRef = useRef<ScannerControls | null>(null);
  const hasDetectedRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [isStarting, setIsStarting] = useState(true);
  const [lastDetected, setLastDetected] = useState<string | null>(null);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const { status } = useBarcodePermission();

  useEffect(() => {
    let cancelled = false;

    async function start() {
      if (!videoRef.current) return;

      try {
        const [{ BrowserMultiFormatReader }, { BarcodeFormat, DecodeHintType }] = await Promise.all([
          import('@zxing/browser'),
          import('@zxing/library'),
        ]);

        if (cancelled) return;

        const hints = new Map();
        hints.set(DecodeHintType.POSSIBLE_FORMATS, [
          BarcodeFormat.CODE_128,
          BarcodeFormat.CODE_39,
          BarcodeFormat.CODE_93,
          BarcodeFormat.ITF,
          BarcodeFormat.CODABAR,
          BarcodeFormat.EAN_13,
          BarcodeFormat.EAN_8,
          BarcodeFormat.UPC_A,
          BarcodeFormat.UPC_E,
          BarcodeFormat.DATA_MATRIX,
          BarcodeFormat.QR_CODE,
          BarcodeFormat.PDF_417,
          BarcodeFormat.AZTEC,
        ]);
        hints.set(DecodeHintType.TRY_HARDER, true);

        const reader = new BrowserMultiFormatReader(hints);

        // Explicit back-camera — op iPad/Android kiest undefined soms de front.
        const constraints: MediaStreamConstraints = {
          audio: false,
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
        };

        const controls = await reader.decodeFromConstraints(
          constraints,
          videoRef.current,
          (result, err, scannerControls) => {
            if (hasDetectedRef.current) return;

            if (result) {
              hasDetectedRef.current = true;
              const code = result.getText();
              setLastDetected(code);
              try {
                navigator.vibrate?.(50);
              } catch {
                // no-op
              }
              scannerControls.stop();
              onDetected(code);
            } else if (err && err.name !== 'NotFoundException') {
              console.warn('[BarcodeScanner] decode error:', err.name, err.message);
            }
          },
        );

        if (cancelled) {
          controls.stop();
          return;
        }

        controlsRef.current = controls;
        setIsStarting(false);
      } catch (err) {
        console.error('[BarcodeScanner] failed to start:', err);
        if (!cancelled) {
          setError(
            err instanceof Error && err.name === 'NotAllowedError'
              ? 'Camera-toegang geweigerd. Geef toestemming in je browser-instellingen.'
              : 'Camera niet beschikbaar. Voer het tracking-nummer handmatig in.',
          );
          setIsStarting(false);
        }
      }
    }

    start();

    return () => {
      cancelled = true;
      if (controlsRef.current) {
        try {
          controlsRef.current.stop();
        } catch (err) {
          console.warn('[BarcodeScanner] stop error:', err);
        }
        controlsRef.current = null;
      }
    };
  }, [onDetected]);

  function handleManualSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = manualCode.trim();
    if (!trimmed) return;
    if (controlsRef.current) {
      try {
        controlsRef.current.stop();
      } catch {
        // no-op
      }
    }
    if (onManualEntry) {
      onManualEntry(trimmed);
    } else {
      onDetected(trimmed);
    }
  }

  return (
    <WbModal
      open
      onClose={onClose}
      variant="volledig"
      sluitbaar={false}
      aria-label="Scan het label"
      className="bg-black"
      acties={
        showManualEntry ? (
          <>
            <WbButton variant="secondary" size="lg" onClick={() => { setShowManualEntry(false); setManualCode(''); }}>Terug</WbButton>
            <WbButton size="lg" type="submit" form="barcode-handmatig" disabled={!manualCode.trim()}>Zoeken</WbButton>
          </>
        ) : (
          <>
            <WbButton variant="secondary" size="lg" onClick={() => setShowManualEntry(true)}>Handmatig</WbButton>
            <WbButton variant="secondary" size="lg" onClick={onClose}>Annuleren</WbButton>
          </>
        )
      }
    >
      <div className="relative h-full w-full bg-black">
        <video ref={videoRef} playsInline autoPlay muted className="absolute inset-0 w-full h-full object-cover" />

        {/* Zoeker-kader: 2 px, met de mint-lijn als scanlijn */}
        {!showManualEntry && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="w-80 h-32 border-wb border-white rounded-wb shadow-[0_0_0_9999px_rgba(0,0,0,0.35)]">
              <div className="h-0.5 bg-wb-mint animate-pulse" />
            </div>
          </div>
        )}

        {/* Kop */}
        <div className="absolute top-0 inset-x-0 px-4 pt-3 pb-6 safe-area-top bg-gradient-to-b from-black/70 to-transparent">
          <p className="font-wb-heading font-bold text-base uppercase tracking-wide text-white">Scan het label</p>
          <p className="text-sm text-white/80">Houd de barcode in het midden</p>
          {lastDetected && <p className="mt-1 font-mono text-sm text-white break-all">Gelezen: {lastDetected}</p>}
        </div>

        {(isStarting || status === 'prompt') && !error && (
          <div className="absolute inset-x-0 top-1/3 text-center font-mono text-sm text-white/80">Camera starten…</div>
        )}

        {error && (
          <div className="absolute inset-x-0 top-1/3 mx-4">
            <WbFlag kind="geld" title="Camera">{error}</WbFlag>
          </div>
        )}

        {/* Handmatig invoeren */}
        {showManualEntry && (
          <form id="barcode-handmatig" onSubmit={handleManualSubmit} className="absolute inset-x-0 bottom-4 mx-4 p-3 bg-wb-vlak border-wb border-wb-rand rounded-wb">
            <WbInput
              label="Tracking-nummer"
              mono
              autoFocus
              value={manualCode}
              onChange={(e) => setManualCode(e.target.value)}
              placeholder="3SKLED..."
              data-wb-autofocus
            />
          </form>
        )}
      </div>
    </WbModal>
  );
}
